import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from "@/components/ui/Modal";
import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import JobForm from "./component/JobForm";
import Skeleton from "@/components/shared/Skeleton";
import { JobService } from "@/services/jobService";
import { parseArray } from "@/utils/parseArray";
import { toLanguageRows } from "@/utils/languageRequirements";
import { to24HourDisplay } from "@/utils/shiftTime";

// Placeholder rows only — the count and the two-column grid match the form's
// first step, nothing more depends on the names.
const FORM_SKELETON_FIELDS = ["title", "deadline", "location", "employeeType", "shift", "vacancies", "experienceMin", "experienceMax", "priority"];

const EditJobForm = ({ open, jobId, onClose, onSuccess, userOptions }) => {
  const [defaultValues, setDefaultValues] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Captured from the job payload so we can render the current assignee's
  // name even when RBAC has excluded them from the caller's assignable list.
  const [currentAssignee, setCurrentAssignee] = useState(null);

  useEffect(() => {
    if (open && jobId) {
      setDefaultValues(null);
      const fetchJob = async () => {
        try {
          const data = await JobService.fetchJobById(jobId);
          const job = data.data;
          const assignee = job.assignments?.[0] ?? null;
          setCurrentAssignee(
            assignee?.user_id
              ? { id: String(assignee.user_id), name: assignee.name || "" }
              : null
          );
          setDefaultValues({
            id: job.id,
            jobTitle: job.title || "",
            deadline: job.deadline || "",
            location: job.location || "",
            employeeType: job.employee_type || "",
            // Structured shift, if the job has one; `shiftTiming` (legacy
            // free-text) is kept only so ShiftSelector can show it as
            // read-only helper text for jobs that predate this feature.
            shiftTiming: job.shift_timing || "",
            shiftType: job.shift_type || "",
            predefinedShiftId: job.predefined_shift_id || "",
            shiftStartTime: to24HourDisplay(job.shift_start_time),
            shiftEndTime: to24HourDisplay(job.shift_end_time),
            shiftTimezone: job.shift_timezone || "",
            calendarTypeId: job.calendar_type_id || "",
            vacancyCount: job.vacancy_count || "",
            experienceMin: job.experience_min ?? "",
            experienceMax: job.experience_max ?? "",
            overview: job.role_overview || "",
            responsibility: job.key_responsibilities || "",
            department: job.department || "",
            division: job.division || "",
            companyWebsite: job.company_link || "",
            currency:job.currency || "",
            currency_sign:job.currency_sign || "",
            statusFlowId: job.status_flow_id || "",
            candidateCount: job.candidate_count ?? 0,
            // Whether the backend would currently accept a status_flow_id
            // change — true as long as every applicant is still at the
            // flow's initial status, not simply "candidateCount === 0".
            canChangeStatusFlow: job.can_change_status_flow ?? true,
            linkedIn: job.company_linkedin_link || "",
            niceToHave: parseArray(job.nice_to_have),
            certifications: parseArray(job.certifications),
            languagesRequired: toLanguageRows(job.languages_required),
            skills: job.skills || [],
            interviewers: job.interviewers || [],
            jobPriority: job.job_priority ?? "",
            assignedTo: job.assignments?.[0]?.user_id ?? "",
            mandatoryFields: job.mandatory_fields || [],
            selectedConfigurable: job.configurable_fields || [],
            selectedInterviewTypes: job.interview_question_types || [],
            selectedBooleanPlatforms: job.boolean_platforms || [],
            // The job's own resolved consent values. No `?? false`: a key the
            // route did not send has to stay `undefined`, so ConsentConfigSelector
            // pre-sets it from the org default and the payload drops it rather
            // than freezing a `false` nobody chose.
            requireApplyEmailConsent: job.require_apply_email_consent,
            requireBulkEmailConsent: job.require_bulk_email_consent,
            requireReuploadEmailConsent: job.require_reupload_email_consent,
            requireJobfitApplyEmailConsent: job.require_jobfit_apply_email_consent,
            // The four `*_inherited` flags, passed through under their own key
            // rather than as form fields — they are not editable, they only
            // decide whether a row says "saving fixes this to this job".
            emailConsentInherited: {
              require_apply_email_consent_inherited: job.require_apply_email_consent_inherited,
              require_bulk_email_consent_inherited: job.require_bulk_email_consent_inherited,
              require_reupload_email_consent_inherited: job.require_reupload_email_consent_inherited,
              require_jobfit_apply_email_consent_inherited: job.require_jobfit_apply_email_consent_inherited,
            },
            // Existing screening questions — carry their ids so the update
            // edits them in place; changes apply to new candidates only. The
            // response is already in display order, and the array's order is
            // what the update sends back, so display_order is not a form value.
            applicationQuestions: (job.application_questions || []).map((q) => ({
              id: q.id,
              question: q.question,
              answerType: q.answer_type,
              isRequired: q.is_required,
            })),
          });
        } catch (error) {
          // Left silent before: `defaultValues` stayed null forever, so
          // `{defaultValues && <JobForm />}` rendered nothing — a blank modal
          // with no error and no way to retry. A network blip or a stale
          // deep link (?edit=<jobId>) both land here.
          console.error("Failed to fetch job:", error);
          toast.error("Failed to load job data.", { position: "bottom-right" });
          onClose();
        }
      };
      fetchJob();
    }
  }, [open, jobId]);

  // If the current assignee is a higher-role user, they're excluded from
  // userOptions by RBAC — so RHFSelect can't render their name in the trigger
  // via the normal option lookup. Compute a read-only fallback label instead;
  // it appears ONLY in the trigger, never in the dropdown list, so the editor
  // sees who the job is assigned to without being able to (re-)pick them.
  const assignedToFallbackLabel = useMemo(() => {
    if (!currentAssignee?.id) return undefined;
    const alreadyListed = (userOptions || []).some(
      (o) => String(o.value) === currentAssignee.id
    );
    if (alreadyListed) return undefined;
    return `${currentAssignee.name || "Unknown"} (current assignee)`;
  }, [userOptions, currentAssignee]);

  const handleUpdateJob = async (formData) => {
    try {
      setIsSubmitting(true);
      const currency = formData.currency || defaultValues?.currency || "";
      const currency_sign = formData.currency_sign || defaultValues?.currency_sign || "";
      // If the current assignedTo value is a higher-role user that isn't in
      // the editor's own assignable list, the editor has no authority to
      // touch that assignment. Strip it from the payload so jobService.updateJob
      // sends assignedUserIds=null → the backend leaves existing assignments
      // untouched (see server/app/api/v2/job/service.py — "If assignedUserIds
      // is omitted, leave existing assignments untouched"). Without this guard
      // the editor's submit would silently claim to re-assign to the
      // higher-role user, even though the trigger only shows their name as
      // read-only info.
      const assignedToInAssignableList = (userOptions || []).some(
        (o) => String(o.value) === String(formData.assignedTo || "")
      );
      const payload = assignedToInAssignableList
        ? { ...formData, currency, currency_sign }
        : { ...formData, assignedTo: "", currency, currency_sign };
      const result = await JobService.updateJob(jobId, payload);
      // The JD save always completes and closes here regardless of whether
      // a re-trigger picker follows — onSuccess
      // just also receives the changed score-affecting fields so the caller
      // can open the picker as a follow-on action, never a blocker.
      onSuccess?.({
        jobId,
        changedScoreFields: result?.data?.match_retrigger?.changed_score_fields || [],
      });
      onClose();
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.detail || error.message || "Failed to update job";
      toast.error(message, { position: "bottom-right" });
      console.error("Failed to update job:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className="h-[88vh] w-[340px] md:w-full flex flex-col">
        <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0" style={{ zoom: 0.95 }}>
          <div>
            <DialogTitle className="text-[15px] font-semibold text-text-primary">Edit Job Posting</DialogTitle>
            <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">Update the details of this job post</DialogDescription>
          </div>
          <DialogClose className="absolute right-5 top-1/2 -translate-y-1/2">
            <span className="w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center text-text-primary hover:bg-background-icon/60 transition-colors">
              <X size={14} strokeWidth={3} />
            </span>
          </DialogClose>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="px-5 py-3" style={{ zoom: 0.95 }}>
            {defaultValues ? (
              <JobForm key={jobId} onClose={onClose} loadingLabel="Updating..." onSubmit={handleUpdateJob} defaultValues={defaultValues} submitLabel="Update Job" isSubmitting={isSubmitting} userOptions={userOptions} assignedToFallbackLabel={assignedToFallbackLabel} />
            ) : (
              /* The job is a network round-trip away, and without this the
                 modal was a header over 88vh of nothing while it loaded. */
              <div className="flex flex-col gap-5" aria-busy="true">
                <Skeleton className="h-9 w-full" />
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {FORM_SKELETON_FIELDS.map((field) => (
                    <div key={field} className="flex flex-col gap-2">
                      <Skeleton className="h-3.5 w-28" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  ))}
                </div>
                <Skeleton className="h-3.5 w-32" />
                <Skeleton className="h-28 w-full" />
                <div className="flex justify-end gap-3">
                  <Skeleton className="h-10 w-24" />
                  <Skeleton className="h-10 w-36" />
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditJobForm;
